import { Clock, Calendar, Users, Film } from "lucide-react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/dashboard/StatCard";
import {
  ModerationQueueCard,
  UpcomingEventsCard,
  CategoryBreakdownCard,
  AutomationHealthCard,
  ActivityFeedCard,
} from "@/components/dashboard/DashboardCards";
import { useStats } from "@/hooks/useStats";
import type { AdminStats } from "@/types";

type MetricTrend = AdminStats["trends"][keyof AdminStats["trends"]];

/**
 * Trend label for a stat card: absolute change over the 14-day window, e.g.
 * "+3 / 14 dni". A flat trend (delta 0) returns no label so StatCard omits the
 * chip entirely — a green up-arrow on "no change" would be misleading.
 */
function trendLabel(trend: MetricTrend): { delta?: string; deltaDir?: "up" | "down" } {
  const { delta } = trend;
  if (delta === 0) return {};
  const sign = delta > 0 ? "+" : "−";
  return { delta: `${sign}${Math.abs(delta)} / 14 dni`, deltaDir: delta > 0 ? "up" : "down" };
}

// Formatters are stateless; safe to share across renders. The current date is
// computed on every render so the greeting line refreshes across midnight if
// the app is left open.
const dayMonthYearFmt = new Intl.DateTimeFormat("pl-PL", {
  day: "numeric",
  month: "long",
  year: "numeric",
});
const weekdayFmt = new Intl.DateTimeFormat("pl-PL", { weekday: "long" });

export default function DashboardPage() {
  const { data: stats, isLoading, error } = useStats();
  const now = new Date();
  const dayMonthYear = dayMonthYearFmt.format(now);
  const weekday = weekdayFmt.format(now);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-brand text-3xl font-bold tracking-tight">Dzień dobry!</h1>
          <p className="mt-1 text-sm text-pulso-muted">
            Twój panel · {dayMonthYear} · {weekday}
          </p>
        </div>
        <Button className="gap-2" asChild>
          <Link to="/events">
            <Calendar className="h-4 w-4" /> Zobacz wydarzenia
          </Link>
        </Button>
      </div>

      {/* Stat cards */}
      {isLoading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-36 animate-pulse rounded-xl bg-black/[0.04]" />
          ))}
        </div>
      )}

      {error && <p className="text-destructive">Nie udało się załadować statystyk.</p>}

      {stats && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={Clock}
            label="Oczekujące wydarzenia"
            value={stats.pendingEvents}
            color="#E85C3A"
            // No delta chip here — a 14-day change in the moderation backlog
            // isn't actionable; the current count + sparkline say enough.
            spark={stats.trends.pendingEvents.series}
            linkTo="/pending"
          />
          <StatCard
            icon={Calendar}
            label="Zatwierdzone wydarzenia"
            value={stats.approvedEvents}
            color="#1F5A9E"
            {...trendLabel(stats.trends.approvedEvents)}
            spark={stats.trends.approvedEvents.series}
            linkTo="/events"
          />
          <StatCard
            icon={Film}
            label="Filmy"
            value={stats.approvedMovies + stats.pendingMovies}
            color="#8C3D99"
            subtitle={`zatwierdzone: ${stats.approvedMovies} / oczekujące: ${stats.pendingMovies}`}
            {...trendLabel(stats.trends.movies)}
            spark={stats.trends.movies.series}
            linkTo="/movies"
          />
          <StatCard
            icon={Users}
            label="Użytkownicy"
            value={stats.totalUsers}
            color="#2D8D57"
            {...trendLabel(stats.trends.users)}
            spark={stats.trends.users.series}
            linkTo="/users"
          />
        </div>
      )}

      {/* Queue + upcoming */}
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[1.4fr_1fr]">
        <ModerationQueueCard />
        <UpcomingEventsCard />
      </div>

      {/* Breakdown + automation + activity */}
      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <CategoryBreakdownCard />
        <AutomationHealthCard />
        <ActivityFeedCard />
      </div>
    </div>
  );
}
